<script lang="ts">
    // @component
    // Favorite button with platter and non-platter variants.

    // The following CSS props can be overridden from parent components.
    // Props marked with `unfavorited` or `favorited` apply to the unfavorited state only.
    // Props marked with `platter only` or `non-platter only` apply to those variants only.

    // @cssprop --favoriteButtonBackground - color of button background
    // @cssprop --favoriteButtonSize - size of favorite button

    // @cssprop --favoriteButtonStarOutline - color of star outline (unfavorited)
    // @cssprop --favoriteButtonStarOutline-hover - color of star outline on hover (unfavorited, non-platter only)

    // @cssprop --favoriteButtonStarFill - color of filled star (favorited)
    // @cssprop --favoriteButtonStarFill-hover - color of filled star on hover (favorited, platter only)

    // @cssprop --favoriteButtonStarCutout - color of circle with star cutout (favorited, platter only)
    // @cssprop --favoriteButtonStarCutout-hover - color of circle with star cutout on hover (favorited, platter only)

    import { createEventDispatcher } from 'svelte';
    import { getI18n } from '~/stores/i18n';
    import FavoriteIconPlatter from '~/components/jet/buttons/FavoriteButton/Platter.svelte';
    import FavoriteIconNonPlatter from '~/components/jet/buttons/FavoriteButton/NonPlatter.svelte';

    const i18n = getI18n();

    /**
     * Whether or not this is favorited
     * @type {boolean}
     */
    export let isFavorited: boolean;

    /**
     * Title text for this button
     * @type {string}
     */
    export let title: string = '';

    /**
     * Whether the button should be styled with platter or not
     */
    export let platter: boolean = true;

    /**
     * Weight of favorite icon (platter-only)
     */
    export let iconWeight: 'regular' | 'bold' = 'regular';

    const dispatch = createEventDispatcher();

    // Alias
    $: inFavorites = isFavorited;
    $: ariaLabel = inFavorites
        ? $i18n.t('ASE.Web.MusicClassical.Favorite.Undo')
        : $i18n.t('ASE.Web.MusicClassical.Favorite');

    const handleToggle = () => dispatch('toggle', !inFavorites);
</script>

<!-- rdar://89773572 (JMOTW: Favoriting – Use checkbox for FavoriteButton) -->
<button
    class="favorite-button"
    class:favorite-button--platter={platter}
    class:favorite-button--non-platter={!platter}
    class:is-favorited={inFavorites}
    aria-label={ariaLabel}
    {title}
    data-testid="favorite-button"
    on:click|stopPropagation={handleToggle}
    on:dblclick|stopPropagation
>
    {#if platter}
        <FavoriteIconPlatter {inFavorites} {iconWeight} />
    {:else}
        <FavoriteIconNonPlatter {inFavorites} />
    {/if}
</button>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use '@amp/web-shared-styles/app/core/mixins/focus' as *;

    .favorite-button {
        // Default to favorite button filling the parent container
        width: var(--favoriteButtonSize, 100%);
        height: var(--favoriteButtonSize, 100%);
        line-height: 0; // Normalize white space

        // Center favorite icon inside button
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--favoriteButtonBackground, transparent);
    }

    // Non-platter button styles
    .favorite-button--non-platter {
        // Unfavorited
        // prettier-ignore
        --favoriteIconStarOutline: var(--favoriteButtonStarOutline, transparent);
        --favoriteIconStarFill: var(--favoriteButtonStarFill, transparent);

        // Keep focus ring within bounds of track container
        &:focus-visible {
            @include focus-shadow-inset;
        }

        // Hover state, unfavorited
        &:hover,
        &:focus-visible {
            --favoriteIconStarOutline: var(
                --favoriteButtonStarOutline-hover,
                var(--keyColor)
            );
        }

        // Favorited
        &.is-favorited {
            --favoriteIconStarFill: var(
                --favoriteButtonStarFill,
                var(--keyColor)
            );
        }
    }

    // Platter button styles
    .favorite-button--platter {
        // Unfavorited
        // Not passing in fallback to var() will use child component's fallback
        --favoriteIconStarOutline: var(--favoriteButtonStarOutline);
        background: var(--favoriteButtonBackground, var(--systemQuinary));
        border-radius: 50%; // Platter = circle!

        // Hover state, unfavorited
        &:hover,
        &:focus-visible {
            --favoriteIconStarOutline: var(
                --favoriteButtonStarOutline-hover,
                white
            );
            background: var(--favoriteButtonBackground-hover, var(--keyColor));
        }

        // Favorited
        &.is-favorited {
            --favoriteIconStarCutout: var(
                --favoriteButtonStarCutout,
                var(--keyColor)
            );
            --favoriteIconStarFill: var(--favoriteButtonStarFill, white);
            background: var(--favoriteButtonBackground, var(--keyColor));
        }

        // Hover state, favorited
        &.is-favorited:hover,
        &.is-favorited:focus-visible {
            --favoriteIconStarCutout: var(
                --favoriteButtonStarCutout-hover,
                var(--keyColor)
            );
            --favoriteIconStarFill: var(--favoriteButtonStarFill-hover, white);
            background: var(--favoriteButtonBackground, var(--keyColor));
        }
    }
</style>
